import { useQuery } from 'react-query';
import { Translation, TranslationTeam } from '@deaf-bible-society-2-0/deafbible-metadata-utils';

import { getTranslation } from '../utils/api/getTranslation';

interface WatchPlanVideoTeamInfo {
  team: TranslationTeam | undefined;
  qualityChecks: string[];
}

/**
 * Find the translation team for a BBB video id within a translation.
 *
 * @param {Translation} translation - Loaded translation metadata.
 * @param {string} bbbVideoId - BBB video Salesforce / gateway id.
 * @returns {TranslationTeam | undefined} Matching team when found.
 */
const findTeamForBbbVideo = (
  translation: Translation,
  bbbVideoId: string
): TranslationTeam | undefined => {
  const matchingBook = translation.books.find(book =>
    book.videos.some(item => item.bbb_id === bbbVideoId)
  );

  if (matchingBook === undefined) {
    return undefined;
  }

  const video = matchingBook.videos.find(item => item.bbb_id === bbbVideoId);
  if (video === undefined) {
    return undefined;
  }

  return translation.teams.find(team => team.id === video.team_id) ?? translation.team;
};

/**
 * Load team + quality checks for a watch plan day's BBB video.
 *
 * @param {string | undefined} translationCode - Translation shortcode (e.g. aslv).
 * @param {string | undefined} bbbVideoId - Selected day's video id.
 * @returns {WatchPlanVideoTeamInfo} Team and quality check labels.
 */
const useWatchPlanVideoTeam = (
  translationCode: string | undefined,
  bbbVideoId: string | undefined
): WatchPlanVideoTeamInfo => {
  const { data } = useQuery<Translation | undefined>(
    ['watch-plan-translation', translationCode],
    () => getTranslation(translationCode),
    {
      enabled: translationCode !== undefined && translationCode.length > 0,
      staleTime: Infinity,
      cacheTime: Infinity,
    }
  );

  if (data === undefined || bbbVideoId === undefined) {
    return { team: undefined, qualityChecks: [] };
  }

  return {
    team: findTeamForBbbVideo(data, bbbVideoId),
    qualityChecks: data.quality_checks ?? [],
  };
};

export default useWatchPlanVideoTeam;
